import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../api/axios";
import { useAuth } from "../context/AuthContext";
import NotificationBell from "./NotificationBell";

const emptyClientForm = {
  name: "",
  company: "",
  phone: "",
  email: "",
  source: "Manual",
  requirement: "",
  assignedTo: "",
};

function parseCsvRow(row) {
  const values = [];
  let current = "";
  let insideQuotes = false;

  for (let i = 0; i < row.length; i += 1) {
    const char = row[i];
    const nextChar = row[i + 1];

    if (char === '"') {
      if (insideQuotes && nextChar === '"') {
        current += '"';
        i += 1;
      } else {
        insideQuotes = !insideQuotes;
      }
    } else if (char === "," && !insideQuotes) {
      values.push(current.trim());
      current = "";
    } else {
      current += char;
    }
  }

  values.push(current.trim());
  return values;
}

function parseCsvText(text) {
  const rows = text
    .replace(/^\uFEFF/, "")
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean);

  if (rows.length < 2) {
    return [];
  }

  const headers = parseCsvRow(rows[0]).map((header) =>
    header.toLowerCase().replace(/[^a-z0-9]+/g, " ").trim()
  );

  return rows.slice(1).map((row) => {
    const values = parseCsvRow(row);
    return headers.reduce((acc, header, index) => {
      acc[header] = values[index] || "";
      return acc;
    }, {});
  });
}

function Clients() {
  const { user, hasPermission } = useAuth();

  // Check if user has full access to all clients (same logic as backend)
  // Only Admin, HR, Manager, HR Manager can see all clients
  // Regular employees with read permission can only see their assigned clients
  const hasFullAccess =
    user?.role?.name === 'Admin' ||
    user?.role?.name === 'HR' ||
    user?.role?.name === 'Manager' ||
    user?.role?.name === 'HR Manager';

  const navigate = useNavigate();
  const [leads, setLeads] = useState([]);
  const [metaLeads, setMetaLeads] = useState([]);
  const [clientRecords, setClientRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [showAddModal, setShowAddModal] = useState(false);
  const [clientForm, setClientForm] = useState(emptyClientForm);
  const [clientFormErrors, setClientFormErrors] = useState({});
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [showViewModal, setShowViewModal] = useState(false);
  const [viewingClient, setViewingClient] = useState(null);
  const [editForm, setEditForm] = useState(emptyClientForm);
  const [editFormErrors, setEditFormErrors] = useState({});
  const [savingClient, setSavingClient] = useState(false);
  const [savingEditClient, setSavingEditClient] = useState(false);
  const [importingCsv, setImportingCsv] = useState(false);
  const [actionMenu, setActionMenu] = useState(null);
  const [prevStats, setPrevStats] = useState({ total: 0, crm: 0, meta: 0, thisMonth: 0 });
  const [employees, setEmployees] = useState([]);
  const [projects, setProjects] = useState([]);
  const [selectedClientForProjects, setSelectedClientForProjects] = useState(null);
  const [selectedProjectForAssignment, setSelectedProjectForAssignment] = useState(null);
  const [showRequirementModal, setShowRequirementModal] = useState(false);
  const [activeCardFilter, setActiveCardFilter] = useState("Total Clients");
  const [showDealerDropdown, setShowDealerDropdown] = useState(false);
  const [showEditDealerDropdown, setShowEditDealerDropdown] = useState(false);
  const [dealerSearchTerm, setDealerSearchTerm] = useState("");
  const [filterDealer, setFilterDealer] = useState("");
  const [openDealerFilter, setOpenDealerFilter] = useState(false);
  const dealerFilterRef = useRef(null);
  const [filterDate, setFilterDate] = useState("");
  const [filterEndDate, setFilterEndDate] = useState("");
  const [openDateFilter, setOpenDateFilter] = useState(false);
  const dateFilterRef = useRef(null);
  const fileInputRef = useRef(null);
  const CLIENTS_PER_PAGE = 10;

  const fetchClients = useCallback(async () => {
    setLoading(true);
    try {
      const [leadsRes, metaRes, clientsRes, employeesRes, projectsRes] = await Promise.allSettled([
        api.get("/leads", { params: { status: "Client" } }),
        api.get("/meta-leads", { params: { status: "Client", limit: 1000 } }),
        api.get("/clients"),
        api.get("/employees"),
        api.get("/projects"),
      ]);

      if (leadsRes.status === "fulfilled") {
        setLeads(leadsRes.value.data || []);
      }

      if (metaRes.status === "fulfilled") {
        setMetaLeads(metaRes.value.data?.leads || []);
      }

      if (clientsRes.status === "fulfilled") {
        setClientRecords(clientsRes.value.data || []);
      }

      if (employeesRes.status === "fulfilled") {
        setEmployees(employeesRes.value.data || []);
      }

      if (projectsRes.status === "fulfilled") {
        setProjects(projectsRes.value.data || []);
      }
    } catch (error) {
      console.error("Failed to fetch clients:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchClients();
  }, [fetchClients]);

  useEffect(() => {
    const handler = (e) => {
      if (dealerFilterRef.current && !dealerFilterRef.current.contains(e.target)) {
        setOpenDealerFilter(false);
      }
      if (dateFilterRef.current && !dateFilterRef.current.contains(e.target)) {
        setOpenDateFilter(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const clients = useMemo(() => {
    const getCreatorName = (creator) => {
      if (!creator) return 'System';
      if (typeof creator === 'object') return `${creator.firstName || ''} ${creator.lastName || ''}`.trim() || creator.username || 'System';
      const emp = employees.find(e => e._id === creator);
      return emp ? `${emp.firstName || ''} ${emp.lastName || ''}`.trim() : 'System';
    };

    // Map legacy leads with status=Client (backward compat)
    const crmClients = leads.map((lead) => ({
      id: `crm-${lead._id}`,
      backendId: lead._id,
      recordType: "crm",
      clientType: "CRM Lead",
      name: lead.name || lead.company || "Untitled Client",
      company: lead.company || "-",
      phone: lead.phone || "-",
      email: lead.email || "-",
      requirement: lead.requirements || lead.notes || "-",
      source: lead.source || "Manual",
      assignedTo: lead.assignedTo
        ? `${lead.assignedTo.firstName || ""} ${lead.assignedTo.lastName || ""}`.trim() || "Unassigned"
        : "Unassigned",
      convertedOn: lead.convertedDate || lead.updatedAt || lead.createdAt,
      createdAt: lead.createdAt,
      addedBy: getCreatorName(lead.createdBy),
    }));

    const metaClients = metaLeads.map((lead) => ({
      id: `meta-${lead._id}`,
      backendId: lead._id,
      recordType: "meta",
      clientType: "Meta Lead",
      name: lead.name || lead.businessCategory || "Untitled Client",
      company: lead.businessCategory || lead.pageName || "-",
      phone: lead.phone || "-",
      email: lead.email || "-",
      requirement: lead.notes || lead.websiteType || "-",
      source: `Meta · ${lead.pageName || "Facebook"}`,
      assignedTo: lead.assignedTo
        ? `${lead.assignedTo.firstName || ""} ${lead.assignedTo.lastName || ""}`.trim() || "Unassigned"
        : "Unassigned",
      convertedOn: lead.updatedAt || lead.createdTime,
      createdAt: lead.createdTime,
      addedBy: getCreatorName(lead.createdBy),
    }));

    // Map proper Client collection records (new pipeline: lead → deal → client)
    const dealClients = clientRecords.map((c) => ({
      id: `client-${c._id}`,
      backendId: c._id,
      recordType: "client",
      clientType: c.relatedMetaLead ? "Meta Lead" : "CRM Lead",
      name: c.name || "Untitled Client",
      company: c.company || "-",
      phone: c.phone || "-",
      email: c.email || "-",
      requirement: c.requirements || c.notes || "-",
      source: c.source || "Lead Conversion",
      dealValue: c.dealValue || 0,
      assignedTo: c.assignedTo
        ? `${c.assignedTo.firstName || ""} ${c.assignedTo.lastName || ""}`.trim() || "Unassigned"
        : "Unassigned",
      convertedOn: c.convertedAt || c.createdAt,
      createdAt: c.createdAt,
      addedBy: getCreatorName(c.createdBy),
    }));

    // Deduplicate: skip legacy leads that already have a proper Client record
    const clientLeadIds = new Set(clientRecords.map(c => c.relatedLead?.toString()).filter(Boolean));
    const clientMetaIds = new Set(clientRecords.map(c => c.relatedMetaLead?.toString()).filter(Boolean));
    const filteredCrm = crmClients.filter(c => !clientLeadIds.has(c.backendId));
    const filteredMeta = metaClients.filter(c => !clientMetaIds.has(c.backendId));

    const allClients = [...dealClients, ...filteredCrm, ...filteredMeta];

    // Apply role-based filtering
    const roleFilteredClients = allClients.filter(client => {
      // Regular employees (non-Admin/HR/Manager): only show clients assigned to this user
      // Backend already filters, but this is an additional safety check
      if (!hasFullAccess && user?._id) {
        // For client records, check assignedTo object
        if (client.recordType === "client") {
          const clientRec = clientRecords.find(c => c._id === client.backendId);
          const assignedId = clientRec?.assignedTo?._id || clientRec?.assignedTo;
          const createdById = clientRec?.createdBy?._id || clientRec?.createdBy;
          return assignedId === user._id || createdById === user._id;
        }
        // For CRM leads, check assignedTo object
        else if (client.recordType === "crm") {
          const lead = leads.find(l => l._id === client.backendId);
          const assignedId = lead?.assignedTo?._id || lead?.assignedTo;
          const createdById = lead?.createdBy?._id || lead?.createdBy;
          return assignedId === user._id || createdById === user._id;
        }
        // For Meta leads, check assignedTo object
        else if (client.recordType === "meta") {
          const metaLead = metaLeads.find(l => l._id === client.backendId);
          const assignedId = metaLead?.assignedTo?._id || metaLead?.assignedTo;
          return assignedId === user._id;
        }
      }
      return true;
    });

    return roleFilteredClients.sort(
      (a, b) => new Date(b.convertedOn) - new Date(a.convertedOn)
    );
  }, [leads, metaLeads, clientRecords, employees, hasFullAccess, user?._id]);

  const filteredClients = useMemo(() => {
    let result = clients;

    if (activeCardFilter === 'Manual Clients') {
      result = result.filter((client) => client.source === "Manual");
    } else if (activeCardFilter === 'Excel Imported') {
      result = result.filter((client) => client.source && client.source.includes("Excel"));
    } else if (activeCardFilter === 'Lead Converted') {
      result = result.filter((client) =>
        client.source && (client.source.includes("Meta") || client.source.includes("Lead Conversion") || client.source.includes("Website"))
      );
    }

    if (filterDealer) {
      result = result.filter((client) => client.assignedTo === filterDealer);
    }

    if (filterDate || filterEndDate) {
      result = result.filter(client => {
        const clientDateStr = new Date(client.convertedOn).toISOString().split('T')[0];

        if (filterDate && filterEndDate) {
          return clientDateStr >= filterDate && clientDateStr <= filterEndDate;
        } else if (filterDate) {
          return clientDateStr >= filterDate;
        } else if (filterEndDate) {
          return clientDateStr <= filterEndDate;
        }
        return true;
      });
    }

    const term = searchTerm.trim().toLowerCase();
    if (!term) return result;

    return result.filter((client) =>
      [
        client.name,
        client.company,
        client.phone,
        client.email,
        client.requirement,
        client.source,
        client.assignedTo,
        client.clientType,
      ]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(term))
    );
  }, [clients, searchTerm, activeCardFilter, filterDealer, filterDate, filterEndDate]);

  const uniqueDealers = useMemo(() => {
    const dealers = new Set(clients.map(c => c.assignedTo).filter(Boolean));
    return Array.from(dealers).sort();
  }, [clients]);

  const stats = useMemo(() => {
    const total = clients.length;
    const manual = clients.filter((client) => client.source === "Manual").length;
    const excel = clients.filter((client) => client.source && client.source.includes("Excel")).length;
    const leadConverted = clients.filter((client) =>
      client.source && (client.source.includes("Meta") || client.source.includes("Lead Conversion") || client.source.includes("Website"))
    ).length;

    return { total, manual, excel, leadConverted };
  }, [clients]);

  // Calculate previous month stats
  const prevMonthStats = useMemo(() => {
    const lastMonthStart = new Date();
    lastMonthStart.setMonth(lastMonthStart.getMonth() - 1);
    lastMonthStart.setDate(1);
    lastMonthStart.setHours(0, 0, 0, 0);

    const lastMonthEnd = new Date();
    lastMonthEnd.setDate(0);
    lastMonthEnd.setHours(23, 59, 59, 999);

    const lastMonthClients = clients.filter(
      (client) => {
        const convertedDate = new Date(client.convertedOn);
        return convertedDate >= lastMonthStart && convertedDate <= lastMonthEnd;
      }
    );

    const total = lastMonthClients.length;
    const manual = lastMonthClients.filter((client) => client.source === "Manual").length;
    const excel = lastMonthClients.filter((client) => client.source && client.source.includes("Excel")).length;
    const leadConverted = lastMonthClients.filter((client) =>
      client.source && (client.source.includes("Meta") || client.source.includes("Lead Conversion") || client.source.includes("Website"))
    ).length;

    return { total, manual, excel, leadConverted };
  }, [clients]);

  // Trend calculation functions
  const calculateChange = (current, previous) => {
    if (previous === 0 && current === 0) return "0%";
    if (previous === 0) return `+${current * 100}%`;
    const change = ((current - previous) / previous) * 100;
    const sign = change > 0 ? "+" : "";
    return `${sign}${Number.isInteger(change) ? change : change.toFixed(1)}%`;
  };

  const getTrendDirection = (current, previous) => {
    return current >= previous;
  };

  const totalChange = calculateChange(stats.total, prevMonthStats.total);
  const manualChange = calculateChange(stats.manual, prevMonthStats.manual);
  const excelChange = calculateChange(stats.excel, prevMonthStats.excel);
  const leadConvertedChange = calculateChange(stats.leadConverted, prevMonthStats.leadConverted);

  const totalTrend = getTrendDirection(stats.total, prevMonthStats.total);
  const manualTrend = getTrendDirection(stats.manual, prevMonthStats.manual);
  const excelTrend = getTrendDirection(stats.excel, prevMonthStats.excel);
  const leadConvertedTrend = getTrendDirection(stats.leadConverted, prevMonthStats.leadConverted);

  const sparklineData = useMemo(() => {
    const now = new Date();
    const months = Array.from({ length: 6 }, (_, i) => {
      const d = new Date(now.getFullYear(), now.getMonth() - (5 - i), 1);
      return { year: d.getFullYear(), month: d.getMonth() };
    });

    const countFor = (filterFn) =>
      months.map(({ year, month }) =>
        clients.filter((c) => {
          const d = new Date(c.convertedOn || c.createdAt);
          return d.getFullYear() === year && d.getMonth() === month && filterFn(c);
        }).length
      );

    return {
      total: countFor(() => true),
      manual: countFor((c) => c.source === "Manual"),
      excel: countFor((c) => c.source && c.source.includes("Excel")),
      leadConverted: countFor((c) => c.source && (c.source.includes("Meta") || c.source.includes("Lead Conversion") || c.source.includes("Website"))),
    };
  }, [clients]);

  const Sparkline = ({ data, color }) => {
    const w = 88, h = 36;
    const max = Math.max(...data, 1);
    const min = Math.min(...data);
    const range = max - min || 1;
    const pts = data
      .map((v, i) => {
        const x = (i / (data.length - 1)) * w;
        const y = h - 4 - ((v - min) / range) * (h - 10);
        return `${x.toFixed(1)},${y.toFixed(1)}`;
      })
      .join(' ');
    const fillPts = `0,${h} ` + pts + ` ${w},${h}`;
    return (
      <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`} style={{ display: 'block', overflow: 'visible' }}>
        <defs>
          <linearGradient id={`sg-${color.replace('#', '')}`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={color} stopOpacity="0.18" />
            <stop offset="100%" stopColor={color} stopOpacity="0" />
          </linearGradient>
        </defs>
        <polygon points={fillPts} fill={`url(#sg-${color.replace('#', '')})`} className="sparkline-fill" />
        <polyline points={pts} fill="none" stroke={color} strokeWidth="1.8"
          strokeLinecap="round" strokeLinejoin="round" className="sparkline-line" />
        {data.map((v, i) => {
          const x = (i / (data.length - 1)) * w;
          const y = h - 4 - ((v - min) / range) * (h - 10);
          return i === data.length - 1 ? (
            <circle key={i} cx={x.toFixed(1)} cy={y.toFixed(1)} r="3" fill={color} className="sparkline-dot" />
          ) : null;
        })}
      </svg>
    );
  };

  const formatDate = (value) => {
    if (!value) return "-";
    return new Date(value).toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const totalPages = Math.max(1, Math.ceil(filteredClients.length / CLIENTS_PER_PAGE));
  const pageStartIndex = (currentPage - 1) * CLIENTS_PER_PAGE;
  const paginatedClients = filteredClients.slice(pageStartIndex, pageStartIndex + CLIENTS_PER_PAGE);
  const canCreateClients = hasPermission?.('clients', 'create');
  const canUpdateClients = hasPermission?.('clients', 'update');
  const canDeleteClients = hasPermission?.('clients', 'delete');
  const canReadClients = hasPermission?.('clients', 'read');

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, clients.length]);

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  useEffect(() => {
    if (!actionMenu) return undefined;

    const handleDocumentClick = (event) => {
      if (!event.target.closest("[data-client-action-menu]")) {
        setActionMenu(null);
      }
    };

    const handleEscape = (event) => {
      if (event.key === "Escape") {
        setActionMenu(null);
      }
    };

    const handleViewportChange = () => {
      setActionMenu(null);
    };

    document.addEventListener("mousedown", handleDocumentClick);
    document.addEventListener("keydown", handleEscape);
    window.addEventListener("resize", handleViewportChange);
    window.addEventListener("scroll", handleViewportChange, true);

    return () => {
      document.removeEventListener("mousedown", handleDocumentClick);
      document.removeEventListener("keydown", handleEscape);
      window.removeEventListener("resize", handleViewportChange);
      window.removeEventListener("scroll", handleViewportChange, true);
    };
  }, [actionMenu]);

  const createConvertedClient = useCallback(async (payload) => {
    // Use provided assignedTo or fallback to current user
    const assignedTo = payload.assignedTo || user?._id || user?.id;

    if (!assignedTo) {
      throw new Error("Your session is missing a user id. Please log in again.");
    }

    // Explicitly map fields to match the backend expectations perfectly
    const clientPayload = {
      name: payload.name.trim(),
      company: payload.company.trim(),
      email: payload.email.trim(),
      phone: payload.phone.trim(),
      source: payload.source.trim(),
      assignedTo: assignedTo,
      requirements: payload.requirement.trim(),
      notes: payload.requirement.trim(),
    };

    // Use the dedicated clients endpoint
    await api.post("/clients", clientPayload);
  }, [user]);

  const handleExportCsv = () => {
    const headers = [
      "Name",
      "Company",
      "Phone",
      "Email",
      "Requirement",
      "Source",
      "Dealer",
      "Type",
      "Converted On",
    ];

    const escape = (value) => {
      const stringValue = String(value ?? "");
      // Clean up any extra spaces or newlines that might break CSV rows
      const cleanValue = stringValue.replace(/\s+/g, ' ').trim();
      return `"${cleanValue.replace(/"/g, '""')}"`;
    };

    const rows = filteredClients.map((client) => [
      client.name || "-",
      client.company || "-",
      client.phone && client.phone !== "-" ? `="\t${client.phone}"` : "-",
      client.email || "-",
      client.requirement || "-",
      client.source || "-",
      client.assignedTo || "Unassigned",
      client.clientType || "-",
      formatDate(client.convertedOn),
    ]);

    const csvContent = [headers, ...rows].map((row) => row.map(escape).join(",")).join("\n");
    const blob = new Blob(["\uFEFF" + csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `clients_${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(link.href);
  };

  const openAddClientModal = () => {
    setClientForm({ ...emptyClientForm });
    setClientFormErrors({});
    setShowAddModal(true);
  };

  const closeAddClientModal = () => {
    if (savingClient) return;
    setClientForm({ ...emptyClientForm });
    setClientFormErrors({});
    setShowAddModal(false);
  };

  const validateClientData = (formData, setErrors) => {
    const errors = {};
    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const phoneDigits = String(formData.phone || "").replace(/\D/g, "");

    if (!formData.name.trim()) errors.name = "Client name is required.";

    if (formData.phone && formData.phone.trim() && phoneDigits.length < 10) errors.phone = "Enter a valid phone number.";
    if (formData.email && formData.email.trim() && !emailPattern.test(formData.email.trim())) errors.email = "Enter a valid email address.";

    setErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const updateFormField = (setForm, setErrors, key, value) => {
    let finalValue = value;
    if (key === "phone") {
      finalValue = value.replace(/[^\d+]/g, "");
    }
    setForm((prev) => ({ ...prev, [key]: finalValue }));

    let error = "";
    if (key === "email" && finalValue.trim()) {
      const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailPattern.test(finalValue.trim())) error = "Enter a valid email address.";
    }
    if (key === "name" && !finalValue.trim()) {
      error = "Client name is required.";
    }
    setErrors((prev) => {
      if (error) return { ...prev, [key]: error };
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  const handleAddClient = async (event) => {
    event.preventDefault();
    if (!validateClientData(clientForm, setClientFormErrors)) return;
    setSavingClient(true);

    try {
      await createConvertedClient(clientForm);
      setClientForm({ ...emptyClientForm });
      setClientFormErrors({});
      setShowAddModal(false);
      await fetchClients();
      alert("Client added successfully.");
    } catch (error) {
      alert(error.response?.data?.message || error.message || "Failed to add client.");
    } finally {
      setSavingClient(false);
    }
  };

  const openEditClientModal = (client) => {
    setEditingClient(client);
    setEditForm({
      name: client.name === "Untitled Client" ? "" : client.name,
      company: client.company === "-" ? "" : client.company,
      phone: client.phone === "-" ? "" : client.phone,
      email: client.email === "-" ? "" : client.email,
      source: client.recordType === "meta" ? client.source : client.source === "-" ? "" : client.source,
      requirement: client.requirement === "-" ? "" : client.requirement,
      assignedTo: clientRecords.find(c => c._id === client.backendId)?.assignedTo?._id ||
        clientRecords.find(c => c._id === client.backendId)?.assignedTo ||
        leads.find(l => l._id === client.backendId)?.assignedTo?._id ||
        leads.find(l => l._id === client.backendId)?.assignedTo || "",
    });
    setEditFormErrors({});
    setShowEditModal(true);
  };

  const closeEditClientModal = () => {
    if (savingEditClient) return;
    setEditingClient(null);
    setEditForm({ ...emptyClientForm });
    setEditFormErrors({});
    setShowEditModal(false);
  };

  const handleEditClient = async (event) => {
    event.preventDefault();
    if (!editingClient) return;
    if (!validateClientData(editForm, setEditFormErrors)) return;

    setSavingEditClient(true);
    try {
      if (editingClient.recordType === "crm") {
        await api.put(`/leads/${editingClient.backendId}`, {
          name: editForm.name.trim(),
          company: editForm.company.trim(),
          phone: editForm.phone.trim(),
          email: editForm.email.trim(),
          source: editForm.source.trim(),
          requirements: editForm.requirement.trim(),
          notes: editForm.requirement.trim(),
          assignedTo: editForm.assignedTo,
          status: "Converted",
        });
      } else if (editingClient.recordType === "client") {
        await api.put(`/clients/${editingClient.backendId}`, {
          name: editForm.name.trim(),
          company: editForm.company.trim(),
          phone: editForm.phone.trim(),
          email: editForm.email.trim(),
          source: editForm.source.trim(),
          requirements: editForm.requirement.trim(),
          notes: editForm.requirement.trim(),
          assignedTo: editForm.assignedTo,
        });
      } else {
        await api.patch(`/meta-leads/${editingClient.backendId}`, {
          status: "Converted",
          notes: editForm.requirement.trim(),
        });
      }

      closeEditClientModal();
      await fetchClients();
      alert("Client updated successfully.");
    } catch (error) {
      alert(error.response?.data?.message || error.message || "Failed to update client.");
    } finally {
      setSavingEditClient(false);
    }
  };

  const handleDeleteClient = async (client) => {
    const clientName = client.name || "this client";
    if (!window.confirm(`Delete ${clientName}? This cannot be undone.`)) return;

    try {
      if (client.recordType === "crm") {
        await api.delete(`/leads/${client.backendId}`);
      } else if (client.recordType === "client") {
        await api.delete(`/clients/${client.backendId}`);
      } else {
        await api.delete(`/meta-leads/${client.backendId}`);
      }
      await fetchClients();
      alert("Client deleted successfully.");
    } catch (error) {
      alert(error.response?.data?.message || error.message || "Failed to delete client.");
    }
  };

  const openProposalComposer = (client) => {
    setActionMenu(null);
    navigate("/proposals", {
      state: {
        createProposal: true,
        clientDraft: {
          title: `${client.company && client.company !== "-" ? client.company : client.name} Proposal`,
          clientName: client.name === "Untitled Client" ? "" : client.name,
          clientEmail: client.email === "-" ? "" : client.email,
          clientPhone: client.phone === "-" ? "" : client.phone,
          clientAddress: client.company === "-" ? "" : client.company,
          contactName: client.name === "Untitled Client" ? "" : client.name,
          notes: client.requirement === "-" ? "" : client.requirement,
          projectScope: client.requirement === "-" ? "" : client.requirement,
        },
      },
    });
  };

  const toggleActionMenu = (event, clientId) => {
    const buttonRect = event.currentTarget.getBoundingClientRect();
    const menuWidth = 160;
    const menuHeight = 134;
    const viewportPadding = 12;
    const spaceBelow = window.innerHeight - buttonRect.bottom;
    const shouldOpenAbove = spaceBelow < menuHeight + 8 && buttonRect.top > menuHeight + 8;

    setActionMenu((prev) => {
      if (prev?.id === clientId) {
        return null;
      }

      return {
        id: clientId,
        top: shouldOpenAbove ? buttonRect.top - menuHeight - 8 : buttonRect.bottom + 8,
        left: Math.min(
          Math.max(viewportPadding, buttonRect.right - menuWidth),
          window.innerWidth - menuWidth - viewportPadding
        ),
      };
    });
  };

  const handleImportClick = () => {
    fileInputRef.current?.click();
  };

  const handleImportCsv = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setImportingCsv(true);

    try {
      const text = await file.text();
      const parsedRows = parseCsvText(text);

      if (!parsedRows.length) {
        throw new Error("CSV is empty or missing data rows.");
      }

      const normalizedRows = parsedRows.map((row) => ({
        name: row.name || row["client name"] || row.client || "",
        company: row.company || row.organization || "",
        phone: row.phone || row.mobile || row["contact number"] || row.contact || "",
        email: row.email || row["email address"] || "",
        source: "Excel Imported",
        requirement: row.requirement || row.requirements || row.notes || row.description || "",
      }));

      const validRows = normalizedRows.filter(
        (row) => row.name && row.phone && row.email && row.source
      );

      if (!validRows.length) {
        throw new Error("Each CSV row must include at least name, phone, email, and source columns.");
      }

      for (const row of validRows) {
        await createConvertedClient(row);
      }

      await fetchClients();
      alert(`${validRows.length} client${validRows.length > 1 ? "s" : ""} imported successfully.`);
    } catch (error) {
      alert(error.response?.data?.message || error.message || "CSV import failed.");
    } finally {
      event.target.value = "";
      setImportingCsv(false);
    }
  };

  if (loading) {
    return (
      <div
        className="page-container"
        style={{
          minHeight: "100vh",
          background: "#f8fafc",
          padding: "28px 28px 40px",
          fontFamily: "'Inter', 'Poppins', 'Segoe UI', sans-serif",
        }}
      >
        <style>{`
          @keyframes clients-skeleton-shimmer {
            0% { background-position: 100% 0; }
            100% { background-position: -100% 0; }
          }

          .clients-skeleton {
            background: linear-gradient(90deg, #eef2f7 25%, #f8fafc 37%, #eef2f7 63%);
            background-size: 400% 100%;
            animation: clients-skeleton-shimmer 1.4s ease infinite;
          }
        `}</style>

        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "22px", gap: "12px", flexWrap: "wrap" }}>
          <div>
            <div className="clients-skeleton" style={{ width: 120, height: 34, borderRadius: 8, marginBottom: 8 }} />
            <div className="clients-skeleton" style={{ width: 320, height: 14, borderRadius: 6 }} />
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
            <div className="clients-skeleton" style={{ width: 122, height: 38, borderRadius: 10 }} />
            <div className="clients-skeleton" style={{ width: 122, height: 38, borderRadius: 10 }} />
            <div className="clients-skeleton" style={{ width: 116, height: 38, borderRadius: 10 }} />
          </div>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: "14px", marginBottom: "22px" }}>
          {Array.from({ length: 4 }).map((_, index) => (
            <div key={index} style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: "12px", padding: "16px 18px" }}>
              <div className="clients-skeleton" style={{ width: "44%", height: 13, borderRadius: 6, marginBottom: 14 }} />
              <div className="clients-skeleton" style={{ width: "28%", height: 34, borderRadius: 8, marginBottom: 12 }} />
              <div className="clients-skeleton" style={{ width: 34, height: 3, borderRadius: 999 }} />
            </div>
          ))}
        </div>

        <div style={{ background: "#fff", borderRadius: "14px", border: "1px solid #e2e8f0", overflow: "hidden", boxShadow: "0 1px 2px rgba(15,23,42,0.04)" }}>
          <div style={{ padding: "14px 16px", borderBottom: "1px solid #f1f5f9", background: "#fafafa", display: "flex", alignItems: "center", justifyContent: "space-between", gap: "10px", flexWrap: "wrap" }}>
            <div className="clients-skeleton" style={{ width: 340, maxWidth: "100%", height: 40, borderRadius: 10 }} />
            <div className="clients-skeleton" style={{ width: 110, height: 14, borderRadius: 6 }} />
          </div>

          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ background: "#fafafa" }}>
                  {Array.from({ length: 8 }).map((_, index) => (
                    <th key={index} style={{ padding: "10px 14px", borderBottom: "1px solid #f1f5f9" }}>
                      <div className="clients-skeleton" style={{ width: `${60 + (index % 3) * 18}px`, height: 10, borderRadius: 5 }} />
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {Array.from({ length: 6 }).map((_, rowIndex) => (
                  <tr key={rowIndex} style={{ borderBottom: rowIndex < 5 ? "1px solid #f8fafc" : "none" }}>
                    <td style={{ padding: "11px 14px" }}>
                      <div className="clients-skeleton" style={{ width: "65%", height: 13, borderRadius: 6, marginBottom: 8 }} />
                      <div className="clients-skeleton" style={{ width: "82%", height: 11, borderRadius: 6 }} />
                    </td>
                    <td style={{ padding: "11px 14px" }}>
                      <div className="clients-skeleton" style={{ width: "58%", height: 12, borderRadius: 6, marginBottom: 8 }} />
                      <div className="clients-skeleton" style={{ width: "78%", height: 11, borderRadius: 6 }} />
                    </td>
                    <td style={{ padding: "11px 14px" }}>
                      <div className="clients-skeleton" style={{ width: "88%", height: 12, borderRadius: 6 }} />
                    </td>
                    <td style={{ padding: "11px 14px" }}>
                      <div className="clients-skeleton" style={{ width: 180, maxWidth: "100%", height: 24, borderRadius: 999 }} />
                    </td>
                    <td style={{ padding: "11px 14px" }}>
                      <div className="clients-skeleton" style={{ width: "68%", height: 12, borderRadius: 6 }} />
                    </td>
                    <td style={{ padding: "11px 14px" }}>
                      <div className="clients-skeleton" style={{ width: 86, height: 24, borderRadius: 999 }} />
                    </td>
                    <td style={{ padding: "11px 14px" }}>
                      <div className="clients-skeleton" style={{ width: "72%", height: 12, borderRadius: 6 }} />
                    </td>
                    <td style={{ padding: "11px 14px" }}>
                      <div style={{ display: "flex", justifyContent: "flex-end", gap: 6 }}>
                        <div className="clients-skeleton" style={{ width: 28, height: 28, borderRadius: 8 }} />
                        <div className="clients-skeleton" style={{ width: 28, height: 28, borderRadius: 8 }} />
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className="page-container"
      style={{
        minHeight: "100vh",
        background: "#f8fafc",
        padding: "28px 28px 40px",
        fontFamily: "'Inter', 'Poppins', 'Segoe UI', sans-serif",
      }}
    >
      <input
        ref={fileInputRef}
        type="file"
        accept=".csv,text/csv"
        onChange={handleImportCsv}
        style={{ display: "none" }}
      />

      <style>{`
        /* Sparkline animations */
        @keyframes sparkline-draw {
          from { stroke-dashoffset: 1; }
          to { stroke-dashoffset: 0; }
        }
        @keyframes sparkline-fade-in {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        .sparkline-line {
          stroke-dasharray: 1;
          stroke-dashoffset: 1;
          animation: sparkline-draw 1.2s ease-out forwards;
        }
        .sparkline-fill {
          opacity: 0;
          animation: sparkline-fade-in 0.8s ease-out 0.3s forwards;
        }
        .sparkline-dot {
          opacity: 0;
          animation: sparkline-fade-in 0.4s ease-out 1s forwards;
        }

        /* Export/Import/Cancel button hover effects - Liquid fill animation */
        .clients-export-btn,
        .clients-import-btn,
        .clients-cancel-btn {
          position: relative;
          isolation: isolate;
          overflow: hidden;
          transition: color 0.35s ease, border-color 0.35s ease;
        }

        .clients-export-btn::before,
        .clients-import-btn::before,
        .clients-cancel-btn::before {
          content: '';
          position: absolute;
          inset: 0;
          background: linear-gradient(90deg, #1EA7FF 0%, #9fdd39 52%, #94d82d 100%);
          transform: translateX(-110%);
          transition: transform 0.55s cubic-bezier(0.22, 0.61, 0.36, 1);
          z-index: 0;
          pointer-events: none;
        }

        .clients-export-btn::after,
        .clients-import-btn::after,
        .clients-cancel-btn::after {
          content: '';
          position: absolute;
          top: -35%;
          left: -38%;
          width: 62%;
          height: 170%;
          background:
            radial-gradient(circle at 30% 50%, rgba(255,255,255,0.30) 0 28%, rgba(255,255,255,0) 58%),
            radial-gradient(circle at 58% 38%, rgba(255,255,255,0.22) 0 24%, rgba(255,255,255,0) 56%),
            radial-gradient(circle at 62% 68%, rgba(255,255,255,0.16) 0 18%, rgba(255,255,255,0) 52%);
          border-radius: 44% 56% 58% 42%;
          transform: translateX(-145%) rotate(8deg);
          transition: transform 0.8s cubic-bezier(0.22, 0.61, 0.36, 1);
          z-index: 0;
          opacity: 0.95;
          pointer-events: none;
        }

        .clients-export-btn:hover,
        .clients-import-btn:hover,
        .clients-cancel-btn:hover {
          color: #1f2937 !important;
          border-color: transparent !important;
        }

        .clients-export-btn:hover::before,
        .clients-import-btn:hover::before,
        .clients-cancel-btn:hover::before {
          transform: translateX(0%);
        }

        .clients-export-btn:hover::after,
        .clients-import-btn:hover::after,
        .clients-cancel-btn:hover::after {
          transform: translateX(255%) rotate(8deg);
        }

        @keyframes clients-bubble-float-large {
          0% { transform: translate(0px, 0px) scale(1); }
          30% { transform: translate(-5px, -7px) scale(1.04); }
          60% { transform: translate(4px, -4px) scale(0.97); }
          100% { transform: translate(0px, 0px) scale(1); }
        }

        @keyframes clients-bubble-float-small {
          0% { transform: translate(0px, 0px) scale(1); }
          35% { transform: translate(6px, -9px) scale(1.06); }
          65% { transform: translate(-4px, -3px) scale(0.95); }
          100% { transform: translate(0px, 0px) scale(1); }
        }

        .clients-metrics-grid > div {
          position: relative;
          overflow: hidden;
          isolation: isolate;
        }

        .clients-metrics-grid > div::before {
          content: '';
          position: absolute;
          z-index: 0;
          width: 90px;
          height: 90px;
          border-radius: 50%;
          background: #667eea;
          opacity: 0.06;
          right: -26px;
          bottom: -26px;
          pointer-events: none;
          animation: clients-bubble-float-large 5.5s ease-in-out infinite;
        }

        .clients-metrics-grid > div::after {
          content: '';
          position: absolute;
          z-index: 0;
          width: 54px;
          height: 54px;
          border-radius: 50%;
          background: #667eea;
          opacity: 0.04;
          right: 36px;
          bottom: -20px;
          pointer-events: none;
          animation: clients-bubble-float-small 4.2s ease-in-out infinite;
        }

        .clients-metrics-grid > div:nth-child(1)::before,
        .clients-metrics-grid > div:nth-child(1)::after {
          background: #667eea;
        }

        .clients-metrics-grid > div:nth-child(2)::before,
        .clients-metrics-grid > div:nth-child(2)::after {
          background: #10b981;
        }

        .clients-metrics-grid > div:nth-child(3)::before,
        .clients-metrics-grid > div:nth-child(3)::after {
          background: #8b5cf6;
        }

        .clients-metrics-grid > div:nth-child(4)::before,
        .clients-metrics-grid > div:nth-child(4)::after {
          background: #f59e0b;
        }

        .clients-metrics-grid > div:nth-child(2)::before { animation-delay: 0.8s; }
        .clients-metrics-grid > div:nth-child(3)::before { animation-delay: 1.5s; }
        .clients-metrics-grid > div:nth-child(4)::before { animation-delay: 2.2s; }
        .clients-metrics-grid > div:nth-child(2)::after { animation-delay: 1.2s; }
        .clients-metrics-grid > div:nth-child(3)::after { animation-delay: 0.5s; }
        .clients-metrics-grid > div:nth-child(4)::after { animation-delay: 1.8s; }
      `}</style>

      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: "22px",
          gap: "12px",
          flexWrap: "wrap",
        }}
      >
        <div>
          <h1
            style={{
              fontSize: "24px",
              fontWeight: 600,
              color: "#0f172a",
              margin: 0,
              letterSpacing: "-0.025em",
            }}
          >
            Clients Management
          </h1>
          <p style={{ fontSize: "13px", color: "#94a3b8", margin: "4px 0 0", fontWeight: 500 }}>
            Converted leads from CRM and Meta are listed here automatically.
          </p>
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
          <div style={{ position: 'relative' }}>
            <NotificationBell filterType="Client" />
          </div>

          {hasFullAccess && (
            <button
              onClick={handleExportCsv}
              className="clients-export-btn"
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "7px",
                padding: "8px 14px",
                border: "1px solid #e2e8f0",
                borderRadius: "10px",
                background: "#fff",
                color: "#475569",
                fontSize: "12px",
                fontWeight: 700,
                cursor: "pointer",
                fontFamily: "'Inter', 'Poppins', 'Segoe UI', sans-serif",
              }}
            >
              <svg style={{ position: "relative", zIndex: 1 }} width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                <polyline points="7 10 12 15 17 10" />
                <line x1="12" y1="15" x2="12" y2="3" />
              </svg>
              <span style={{ position: "relative", zIndex: 1 }}>Export CSV</span>
            </button>
          )}

          {hasFullAccess && (
            <button
              onClick={handleImportClick}
              disabled={importingCsv}
              className="clients-import-btn"
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "7px",
                padding: "8px 14px",
                border: "1px solid #e2e8f0",
                borderRadius: "10px",
                background: "#fff",
                color: "#475569",
                fontSize: "12px",
                fontWeight: 700,
                cursor: importingCsv ? "not-allowed" : "pointer",
                opacity: importingCsv ? 0.6 : 1,
                fontFamily: "'Inter', 'Poppins', 'Segoe UI', sans-serif",
              }}
            >
              <span style={{ position: "relative", zIndex: 1, display: "inline-flex", alignItems: "center" }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                  <polyline points="17 8 12 3 7 8" />
                  <line x1="12" y1="3" x2="12" y2="15" />
                </svg>
              </span>
              <span style={{ position: "relative", zIndex: 1 }}>{importingCsv ? "Importing..." : "Import CSV"}</span>
            </button>
          )}

          {canCreateClients && <button
            onClick={openAddClientModal}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "7px",
              padding: "8px 14px",
              border: "none",
              borderRadius: "10px",
              background: "linear-gradient(135deg, #056098 0%, #56a07a 100%)",
              color: "#fff",
              fontSize: "12px",
              fontWeight: 500,
              cursor: "pointer",
              fontFamily: "'Inter', 'Poppins', 'Segoe UI', sans-serif",
            }}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="12" y1="5" x2="12" y2="19" />
              <line x1="5" y1="12" x2="19" y2="12" />
            </svg>
            Add Client
          </button>}
        </div>
      </div>

      <div
        className="clients-metrics-grid"
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
          gap: "14px",
          marginBottom: "22px",
        }}
      >
        {[
          { label: "Total Clients", value: stats.total, color: "#667eea", trend: totalChange, isUp: totalTrend, spark: sparklineData.total },
          { label: "Manual Clients", value: stats.manual, color: "#10b981", trend: manualChange, isUp: manualTrend, spark: sparklineData.manual },
          { label: "Excel Imported", value: stats.excel, color: "#8b5cf6", trend: excelChange, isUp: excelTrend, spark: sparklineData.excel },
          { label: "Lead Converted", value: stats.leadConverted, color: "#f59e0b", trend: leadConvertedChange, isUp: leadConvertedTrend, spark: sparklineData.leadConverted },
        ].map((item) => (
          <div
            key={item.label}
            onClick={() => setActiveCardFilter(activeCardFilter === item.label ? 'Total Clients' : item.label)}
            style={{
              background: "#fff",
              border: activeCardFilter === item.label ? `2px solid ${item.color}` : "1px solid #e2e8f0",
              borderRadius: "12px",
              padding: activeCardFilter === item.label ? "19px 21px 15px" : "20px 22px 16px",
              minHeight: "115px",
              overflow: "hidden",
              position: "relative",
              cursor: "pointer",
              transition: "all 0.2s ease",
              boxShadow: activeCardFilter === item.label ? `0 4px 12px ${item.color}20` : "none"
            }}
          >
            {/* Top row: label + trend badge */}
            <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: "6px" }}>
              <div style={{ fontSize: "11.5px", color: "#64748b", fontWeight: 600, lineHeight: 1.3 }}>
                {item.label}
              </div>
              <span style={{
                fontSize: "10px", fontWeight: 700,
                padding: "2px 6px", borderRadius: "20px",
                background: item.isUp ? "rgba(168,230,61,0.15)" : "rgba(239,68,68,0.1)",
                color: item.isUp ? "#3d7c00" : "#dc2626",
                whiteSpace: "nowrap",
              }}>
                {item.isUp ? "▲" : "▼"} {item.trend}
              </span>
            </div>

            {/* Value */}
            <div style={{ fontSize: "28px", fontWeight: 300, color: "#0f172a", lineHeight: 1, marginBottom: "10px" }}>
              {item.value}
            </div>

            {/* Bottom row: sparkline + from last month */}
            <div style={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", gap: "8px" }}>
              <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-start" }}>
                <span style={{ fontSize: "13px", fontWeight: 700, color: item.isUp ? "#16a34a" : "#ef4444", lineHeight: 1.2 }}>
                  {item.isUp ? "+" : ""}{item.trend}
                </span>
                <span style={{ fontSize: "10px", color: "#94a3b8", lineHeight: 1.4 }}>vs last month</span>
              </div>
              <Sparkline data={item.spark} color={item.color} />
            </div>
          </div>
        ))}
      </div>

      <div
        style={{
          background: "#fff",
          borderRadius: "14px",
          border: "1px solid #e2e8f0",
          boxShadow: "0 1px 2px rgba(15,23,42,0.04)",
        }}
      >
        <div
          style={{
            padding: "14px 16px",
            borderBottom: "1px solid #f1f5f9",
            background: "#fafafa",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: "10px",
            flexWrap: "wrap",
          }}
        >
          <div style={{ display: "flex", gap: "10px", alignItems: "center", flexWrap: "wrap", flex: 1 }}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                background: "#fff",
                border: "1px solid #e2e8f0",
                borderRadius: "10px",
                padding: "8px 12px",
                minWidth: "240px",
                maxWidth: "340px",
                width: "100%",
              }}
            >
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="11" cy="11" r="8" />
                <path d="m21 21-4.35-4.35" />
              </svg>
              <input
                type="text"
                placeholder="Search clients by name, company, phone..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={{
                  border: "none",
                  outline: "none",
                  background: "transparent",
                  width: "100%",
                  fontSize: "12px",
                  color: "#0f172a",
                  fontFamily: "'Inter', 'Poppins', 'Segoe UI', sans-serif",
                }}
              />
            </div>

            <div ref={dealerFilterRef} style={{ position: "relative" }}>
              <div
                onClick={() => setOpenDealerFilter(!openDealerFilter)}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "5px",
                  padding: "6px 10px",
                  background: filterDealer ? "#f0fdf4" : "#fff",
                  border: `1px solid ${filterDealer ? "#056098" : "#e2e8f0"}`,
                  borderRadius: "8px",
                  cursor: "pointer",
                  fontSize: "11px",
                  fontWeight: 500,
                  color: filterDealer ? "#056098" : "#475569",
                  fontFamily: "'Inter','Poppins','Segoe UI',sans-serif",
                  transition: "all 0.15s",
                  whiteSpace: "nowrap"
                }}
              >
                {filterDealer ? filterDealer : "All Dealers"}
                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="6 9 12 15 18 9" /></svg>
              </div>
              {openDealerFilter && (
                <div style={{ position: "absolute", top: "calc(100% + 6px)", left: 0, background: "#fff", border: "1px solid #e2e8f0", borderRadius: "12px", boxShadow: "0 10px 25px rgba(15,23,42,0.1)", padding: "8px", zIndex: 100, minWidth: "160px", display: "flex", flexDirection: "column", gap: "2px" }}>
                  <div style={{ fontSize: "10px", fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", padding: "4px 8px 6px", borderBottom: "1px solid #f1f5f9", marginBottom: "4px" }}>Dealer</div>
                  <div
                    onClick={() => { setFilterDealer(""); setOpenDealerFilter(false); }}
                    style={{ padding: "8px 12px", borderRadius: "8px", fontSize: "12px", color: !filterDealer ? "#15803d" : "#475569", background: !filterDealer ? "#f0fdf4" : "transparent", fontWeight: !filterDealer ? 600 : 500, cursor: "pointer", display: "flex", alignItems: "center", gap: "8px" }}
                    onMouseEnter={(e) => { if (filterDealer) e.currentTarget.style.background = "#f1f5f9"; }}
                    onMouseLeave={(e) => { if (filterDealer) e.currentTarget.style.background = "transparent"; }}
                  >
                    All Dealers
                  </div>
                  {uniqueDealers.map((dlr) => (
                    <div
                      key={dlr}
                      onClick={() => { setFilterDealer(dlr); setOpenDealerFilter(false); }}
                      style={{ padding: "8px 12px", borderRadius: "8px", fontSize: "12px", color: filterDealer === dlr ? "#15803d" : "#475569", background: filterDealer === dlr ? "#f0fdf4" : "transparent", fontWeight: filterDealer === dlr ? 600 : 500, cursor: "pointer", display: "flex", alignItems: "center", gap: "8px" }}
                      onMouseEnter={(e) => { if (filterDealer !== dlr) e.currentTarget.style.background = "#f1f5f9"; }}
                      onMouseLeave={(e) => { if (filterDealer !== dlr) e.currentTarget.style.background = "transparent"; }}
                    >
                      {dlr}
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div ref={dateFilterRef} style={{ position: "relative" }}>
              <div
                onClick={() => setOpenDateFilter(!openDateFilter)}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "5px",
                  padding: "6px 10px",
                  background: (filterDate || filterEndDate) ? "#f0fdf4" : "#fff",
                  border: `1px solid ${(filterDate || filterEndDate) ? "#056098" : "#e2e8f0"}`,
                  borderRadius: "8px",
                  cursor: "pointer",
                  fontSize: "11px",
                  fontWeight: 500,
                  color: (filterDate || filterEndDate) ? "#056098" : "#475569",
                  fontFamily: "'Inter','Poppins','Segoe UI',sans-serif",
                  transition: "all 0.15s",
                  whiteSpace: "nowrap"
                }}
              >
                {filterDate && filterEndDate ? `${new Date(filterDate).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })} - ${new Date(filterEndDate).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}` : filterDate ? `>= ${new Date(filterDate).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}` : filterEndDate ? `<= ${new Date(filterEndDate).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}` : "Any Date"}
                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="6 9 12 15 18 9" /></svg>
              </div>
              {openDateFilter && (
                <div style={{ position: "absolute", top: "calc(100% + 6px)", left: 0, background: "#fff", border: "1px solid #e2e8f0", borderRadius: "12px", boxShadow: "0 10px 25px rgba(15,23,42,0.1)", padding: "12px", zIndex: 100, minWidth: "200px", display: "flex", flexDirection: "column", gap: "8px" }}>
                  <div style={{ fontSize: "10px", fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", padding: "0 2px" }}>Start Date</div>
                  <input
                    type="date"
                    value={filterDate}
                    onChange={e => setFilterDate(e.target.value)}
                    style={{ width: "100%", padding: "6px 10px", border: "1px solid #cbd5e1", borderRadius: "8px", fontSize: "12px", fontFamily: "'Inter','Poppins','Segoe UI',sans-serif", outline: "none", boxSizing: "border-box" }}
                  />
                  <div style={{ fontSize: "10px", fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", padding: "0 2px", marginTop: "4px" }}>End Date</div>
                  <input
                    type="date"
                    value={filterEndDate}
                    onChange={e => setFilterEndDate(e.target.value)}
                    style={{ width: "100%", padding: "6px 10px", border: "1px solid #cbd5e1", borderRadius: "8px", fontSize: "12px", fontFamily: "'Inter','Poppins','Segoe UI',sans-serif", outline: "none", boxSizing: "border-box", marginBottom: "4px" }}
                  />
                  <div style={{ display: "flex", gap: "6px" }}>
                    <button onClick={() => setOpenDateFilter(false)} style={{ flex: 1, padding: "6px", border: "1px solid #e2e8f0", borderRadius: "8px", background: "#056098", color: "#fff", fontSize: "11px", fontWeight: 600, cursor: "pointer" }}>Apply</button>
                    {(filterDate || filterEndDate) && (
                      <button onClick={() => { setFilterDate(""); setFilterEndDate(""); setOpenDateFilter(false); }} style={{ flex: 1, padding: "6px", border: "1px solid #e2e8f0", borderRadius: "8px", background: "#f8fafc", color: "#dc2626", fontSize: "11px", fontWeight: 600, cursor: "pointer" }}>Clear</button>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>

          <div style={{ fontSize: "12px", color: "#94a3b8", fontWeight: 600 }}>
            Showing {filteredClients.length === 0 ? 0 : pageStartIndex + 1}-{Math.min(pageStartIndex + paginatedClients.length, filteredClients.length)} of {filteredClients.length}
          </div>
        </div>

        {filteredClients.length === 0 ? (
          <div style={{ padding: "64px 24px", textAlign: "center" }}>
            <div
              style={{
                width: "56px",
                height: "56px",
                borderRadius: "16px",
                background: "#f8fafc",
                border: "1px solid #e2e8f0",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                margin: "0 auto 16px",
              }}
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                <circle cx="9" cy="7" r="4" />
                <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                <path d="M16 3.13a4 4 0 0 1 0 7.75" />
              </svg>
            </div>
            <div style={{ fontSize: "15px", fontWeight: 700, color: "#0f172a", marginBottom: "6px" }}>
              No converted clients found
            </div>
            <div style={{ fontSize: "12px", color: "#94a3b8" }}>
              Leads with status Converted will appear here automatically.
            </div>
          </div>
        ) : (
          <div style={{ overflowX: "auto", scrollbarWidth: "thin", scrollbarColor: "#cbd5e1 transparent" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", minWidth: "1000px" }}>
              <thead>
                <tr style={{ background: "#fafafa" }}>
                  {[
                    "#",
                    "Client",
                    "Contact",
                    "Requirement",
                    "Source",
                    "Dealer",
                    "Type",
                    "Converted On",
                    "Added By",
                    "Actions",
                  ].map((label) => (
                    <th
                      key={label}
                      style={{
                        padding: "9px 12px",
                        textAlign: "left",
                        fontSize: "9.5px",
                        fontWeight: 700,
                        color: "#94a3b8",
                        textTransform: "uppercase",
                        letterSpacing: "0.04em",
                        borderBottom: "1px solid #f1f5f9",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {paginatedClients.map((client, index) => (
                  <tr
                    key={client.id}
                    onClick={() => setSelectedClientForProjects(client)}
                    style={{
                      borderBottom: index < paginatedClients.length - 1 ? "1px solid #f8fafc" : "none",
                      cursor: "pointer",
                      background: "transparent",
                      transition: "background 0.2s"
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = "#f1f5f9";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = "transparent";
                    }}
                  >
                    <td style={{ padding: "9px 12px", width: "40px" }}>
                      <div style={{ fontSize: "11px", fontWeight: 600, color: "#94a3b8" }}>
                        {String(pageStartIndex + index + 1).padStart(2, '0')}
                      </div>
                    </td>
                    <td style={{ padding: "9px 12px" }}>
                      <div>
                        <div style={{ fontSize: "11.5px", fontWeight: 600, color: "#0f172a", marginBottom: "2px" }}>
                          {client.name}
                        </div>
                        <div style={{ fontSize: "10px", color: "#94a3b8" }}>{client.company}</div>
                      </div>
                    </td>
                    <td style={{ padding: "9px 12px" }}>
                      <div style={{ fontSize: "11px", color: "#475569", marginBottom: "2px" }}>{client.phone}</div>
                      <div style={{ fontSize: "10px", color: "#94a3b8" }}>{client.email}</div>
                    </td>
                    <td style={{ padding: "9px 12px", maxWidth: "170px" }}>
                      <div style={{ fontSize: "11px", color: "#475569", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                        {client.requirement}
                      </div>
                    </td>
                    <td style={{ padding: "9px 12px" }}>
                      {(() => {
                        let s = client.source || "Manual";
                        // Rename Meta to Lead Conversion for display
                        if (s === "Meta") s = "Lead Conversion";

                        let bg = "#f1f5f9", text = "#475569";
                        if (s === "Manual") { bg = "#eff6ff"; text = "#2563eb"; }
                        else if (s === "Lead Conversion") { bg = "#f0fdf4"; text = "#16a34a"; }
                        else if (s.includes("Excel")) { bg = "#fff7ed"; text = "#ea580c"; }
                        else if (s === "Website") { bg = "#ecfeff"; text = "#0891b2"; }

                        return (
                          <span
                            style={{
                              display: "inline-flex",
                              alignItems: "center",
                              padding: "3px 10px",
                              borderRadius: "999px",
                              fontSize: "10px",
                              fontWeight: 700,
                              color: text,
                              background: bg,
                              border: `1px solid ${text}20`,
                              whiteSpace: "nowrap",
                            }}
                          >
                            {s}
                          </span>
                        );
                      })()}
                    </td>
                    <td style={{ padding: "9px 12px", fontSize: "11px", color: "#475569", fontWeight: 500 }}>
                      {client.assignedTo}
                    </td>
                    <td style={{ padding: "9px 12px" }}>
                      <span
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: "6px",
                          padding: "3px 8px",
                          borderRadius: "999px",
                          fontSize: "10px",
                          fontWeight: 700,
                          color: "#166534",
                          background: "#dcfce7",
                        }}
                      >
                        <span style={{ width: "6px", height: "6px", borderRadius: "50%", background: "#22c55e" }} />
                        {client.clientType}
                      </span>
                    </td>
                    <td style={{ padding: "9px 12px", fontSize: "10px", color: "#64748b", fontWeight: 600, whiteSpace: "nowrap" }}>
                      {formatDate(client.convertedOn)}
                    </td>
                    <td style={{ padding: "9px 12px" }}>
                      <span
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          padding: "3px 10px",
                          borderRadius: "999px",
                          fontSize: "9.5px",
                          fontWeight: 700,
                          color: "#8b5cf6",
                          background: "#f3e8ff",
                          border: "1px solid #e9d5ff",
                          whiteSpace: "nowrap"
                        }}
                      >
                        {client.addedBy}
                      </span>
                    </td>
                    <td style={{ padding: "9px 12px" }}>
                      <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end" }} data-client-action-menu>
                        <button
                          onClick={(event) => {
                            event.stopPropagation();
                            toggleActionMenu(event, client.id);
                          }}
                          title="Client actions"
                          style={{
                            width: "28px",
                            height: "28px",
                            borderRadius: "8px",
                            border: "1px solid #e2e8f0",
                            background: "#fff",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            cursor: "pointer",
                            color: "#475569",
                            boxShadow: actionMenu?.id === client.id ? "0 8px 18px rgba(15, 23, 42, 0.08)" : "none",
                          }}
                        >
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                            <circle cx="12" cy="5" r="1.8" />
                            <circle cx="12" cy="12" r="1.8" />
                            <circle cx="12" cy="19" r="1.8" />
                          </svg>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {filteredClients.length > 0 && totalPages > 1 && (
          <div
            style={{
              padding: "12px 16px",
              borderTop: "1px solid #f1f5f9",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: "10px",
              flexWrap: "wrap",
            }}
          >
            <span style={{ fontSize: "11px", color: "#94a3b8", fontWeight: 600 }}>
              Page {currentPage} of {totalPages}
            </span>

            <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
              <button
                onClick={() => setCurrentPage((page) => Math.max(1, page - 1))}
                disabled={currentPage === 1}
                style={{
                  minWidth: "64px",
                  height: "28px",
                  borderRadius: "8px",
                  border: "1px solid #e2e8f0",
                  background: "#fff",
                  color: "#64748b",
                  fontSize: "11px",
                  fontWeight: 700,
                  cursor: currentPage === 1 ? "not-allowed" : "pointer",
                  opacity: currentPage === 1 ? 0.45 : 1,
                }}
              >
                Prev
              </button>

              {Array.from({ length: Math.min(5, totalPages) }, (_, index) => {
                let pageNumber;
                if (totalPages <= 5) {
                  pageNumber = index + 1;
                } else if (currentPage <= 3) {
                  pageNumber = index + 1;
                } else if (currentPage >= totalPages - 2) {
                  pageNumber = totalPages - 4 + index;
                } else {
                  pageNumber = currentPage - 2 + index;
                }

                return (
                  <button
                    key={pageNumber}
                    onClick={() => setCurrentPage(pageNumber)}
                    style={{
                      width: "28px",
                      height: "28px",
                      borderRadius: "8px",
                      border: pageNumber === currentPage ? "1px solid #a3e635" : "1px solid #e2e8f0",
                      background: pageNumber === currentPage ? "#a3e635" : "#fff",
                      color: pageNumber === currentPage ? "#fff" : "#64748b",
                      fontSize: "11px",
                      fontWeight: 700,
                      cursor: "pointer",
                    }}
                  >
                    {pageNumber}
                  </button>
                );
              })}

              <button
                onClick={() => setCurrentPage((page) => Math.min(totalPages, page + 1))}
                disabled={currentPage === totalPages}
                style={{
                  minWidth: "64px",
                  height: "28px",
                  borderRadius: "8px",
                  border: "1px solid #e2e8f0",
                  background: "#fff",
                  color: "#64748b",
                  fontSize: "11px",
                  fontWeight: 700,
                  cursor: currentPage === totalPages ? "not-allowed" : "pointer",
                  opacity: currentPage === totalPages ? 0.45 : 1,
                }}
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>


      {actionMenu && (() => {
        const activeClient = clients.find((client) => client.id === actionMenu.id);
        if (!activeClient) return null;

        return (
          <div
            data-client-action-menu
            style={{
              position: "fixed",
              top: actionMenu.top,
              left: actionMenu.left,
              width: "160px",
              background: "#fff",
              border: "1px solid #e2e8f0",
              borderRadius: "12px",
              boxShadow: "0 18px 40px rgba(15, 23, 42, 0.12)",
              padding: "6px",
              zIndex: 1200,
            }}
          >
            {canReadClients && (
              <button
                onClick={() => {
                  setActionMenu(null);
                  setViewingClient(activeClient);
                  setShowViewModal(true);
                }}
                style={{
                  width: "100%",
                  border: "none",
                  background: "transparent",
                  borderRadius: "8px",
                  padding: "9px 10px",
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  fontSize: "11px",
                  fontWeight: 600,
                  color: "#334155",
                  cursor: "pointer",
                  textAlign: "left",
                }}
              >
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#16a34a" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                  <circle cx="12" cy="12" r="3" />
                </svg>
                View details
              </button>
            )}
            {hasPermission('proposals', 'create') && (
              <button
                onClick={() => openProposalComposer(activeClient)}
                style={{
                  width: "100%",
                  border: "none",
                  background: "transparent",
                  borderRadius: "8px",
                  padding: "9px 10px",
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  fontSize: "11px",
                  fontWeight: 600,
                  color: "#334155",
                  cursor: "pointer",
                  textAlign: "left",
                }}
              >
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#3b82f6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M4 4h16v16H4z" />
                  <path d="M8 9h8" />
                  <path d="M8 13h8" />
                  <path d="M8 17h5" />
                </svg>
                Proposal
              </button>
            )}
            {canUpdateClients && <button
              onClick={() => {
                setActionMenu(null);
                openEditClientModal(activeClient);
              }}
              style={{
                width: "100%",
                border: "none",
                background: "transparent",
                borderRadius: "8px",
                padding: "9px 10px",
                display: "flex",
                alignItems: "center",
                gap: "8px",
                fontSize: "11px",
                fontWeight: 600,
                color: "#334155",
                cursor: "pointer",
                textAlign: "left",
              }}
            >
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#2563eb" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 20h9" />
                <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4 12.5-12.5z" />
              </svg>
              Edit client
            </button>}
            {canDeleteClients && (
              <button
                onClick={() => {
                  setActionMenu(null);
                  handleDeleteClient(activeClient);
                }}
                style={{
                  width: "100%",
                  border: "none",
                  background: "transparent",
                  borderRadius: "8px",
                  padding: "9px 10px",
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  fontSize: "11px",
                  fontWeight: 600,
                  color: "#dc2626",
                  cursor: "pointer",
                  textAlign: "left",
                }}
              >
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#e11d48" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="3 6 5 6 21 6" />
                  <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
                  <path d="M10 11v6" />
                  <path d="M14 11v6" />
                  <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
                </svg>
                Delete client
              </button>
            )}
          </div>
        );
      })()}
      {showAddModal && (
        <div
          onClick={closeAddClientModal}
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(15, 23, 42, 0.45)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "20px",
            zIndex: 1000,
          }}
        >
          <div
            onClick={(event) => event.stopPropagation()}
            style={{
              width: "100%",
              maxWidth: "680px",
              background: "#fff",
              borderRadius: "18px",
              border: "1px solid #e2e8f0",
              boxShadow: "0 20px 40px rgba(15,23,42,0.18)",
              overflow: "hidden",
            }}
          >
            <div style={{ padding: "18px 22px", borderBottom: "1px solid #f1f5f9", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div>
                <div style={{ fontSize: "18px", fontWeight: 600, color: "#0f172a" }}>Add Client</div>
                <div style={{ fontSize: "12px", color: "#94a3b8", marginTop: "4px" }}>
                  New clients are created as converted CRM records.
                </div>
              </div>
              <button
                onClick={closeAddClientModal}
                style={{ background: "transparent", border: "none", fontSize: "24px", color: "#94a3b8", cursor: "pointer", lineHeight: 1 }}
              >
                ×
              </button>
            </div>

            <form onSubmit={handleAddClient}>
              <div style={{ padding: "24px 28px", display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: "18px" }}>
                {[
                  { label: "Client Name", key: "name", required: true, placeholder: "Enter client name", type: "text" },
                  { label: "Company", key: "company", required: false, placeholder: "Enter company name", type: "text" },
                  { label: "Phone", key: "phone", required: false, placeholder: "Enter phone number", type: "tel" },
                  { label: "Email", key: "email", required: false, placeholder: "Enter email address", type: "email" },
                  { label: "Source", key: "source", required: false, placeholder: "Manual", type: "text" },
                ].map((field) => (
                  <label key={field.key} style={{ display: "flex", flexDirection: "column", gap: "6px", fontSize: "12px", fontWeight: 700, color: "#334155" }}>
                    <span>{field.label} {field.required && <span style={{ color: "#ef4444" }}>*</span>}</span>
                    <input
                      type={field.type}
                      required={field.required}
                      value={clientForm[field.key]}
                      onChange={(event) => updateFormField(setClientForm, setClientFormErrors, field.key, event.target.value)}
                      placeholder={field.placeholder}
                      aria-invalid={Boolean(clientFormErrors[field.key])}
                      style={{
                        height: "42px",
                        borderRadius: "10px",
                        border: clientFormErrors[field.key] ? "1px solid #ef4444" : "1px solid #e2e8f0",
                        padding: "0 14px",
                        fontSize: "13px",
                        color: "#0f172a",
                        fontFamily: "'Inter', 'Poppins', 'Segoe UI', sans-serif",
                      }}
                    />
                    {clientFormErrors[field.key] && (
                      <span style={{ fontSize: "11px", color: "#dc2626", fontWeight: 600 }}>
                        {clientFormErrors[field.key]}
                      </span>
                    )}
                  </label>
                ))}

                <label style={{ display: "flex", flexDirection: "column", gap: "6px", fontSize: "12px", fontWeight: 700, color: "#334155" }}>
                  Dealer
                  <div style={{ position: "relative" }}>
                    <div
                      onClick={() => setShowDealerDropdown(!showDealerDropdown)}
                      style={{
                        height: "42px",
                        borderRadius: "10px",
                        border: clientFormErrors.assignedTo ? "1px solid #ef4444" : "1px solid #e2e8f0",
                        padding: "0 14px",
                        fontSize: "13px",
                        color: "#0f172a",
                        background: "#fff",
                        fontFamily: "'Inter', 'Poppins', 'Segoe UI', sans-serif",
                        display: "flex",
                        alignItems: "center",
                        cursor: "pointer",
                        justifyContent: "space-between"
                      }}
                    >
                      <span>
                        {clientForm.assignedTo
                          ? (() => {
                            const emp = employees.find((e) => e._id === clientForm.assignedTo);
                            return emp ? `${emp.firstName} ${emp.lastName}` : "Select Dealer";
                          })()
                          : "Select Dealer"}
                      </span>
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9"></polyline></svg>
                    </div>

                    {showDealerDropdown && (
                      <div style={{ position: "absolute", top: "100%", left: 0, width: "100%", background: "#fff", zIndex: 100, border: "1px solid #e2e8f0", borderRadius: "10px", marginTop: "4px", boxShadow: "0 10px 15px -3px rgba(0, 0, 0, 0.1)", padding: "6px" }}>
                        <div style={{ marginBottom: "6px" }}>
                          <input
                            type="text"
                            placeholder="Search dealers..."
                            value={dealerSearchTerm}
                            onChange={e => setDealerSearchTerm(e.target.value)}
                            onClick={e => e.stopPropagation()}
                            style={{ width: "100%", border: "none", background: "#f8fafc", padding: "8px 12px", borderRadius: "8px", fontSize: "12px", outline: "none", fontFamily: "'Inter', 'Poppins', 'Segoe UI', sans-serif" }}
                          />
                        </div>
                        <div style={{ maxHeight: "160px", overflowY: "auto", display: "flex", flexDirection: "column", gap: "2px" }}>
                          <div
                            onClick={() => { updateFormField(setClientForm, setClientFormErrors, "assignedTo", ""); setShowDealerDropdown(false); setDealerSearchTerm(""); }}
                            style={{ padding: "8px 12px", fontSize: "12px", cursor: "pointer", background: !clientForm.assignedTo ? "#dcfce7" : "transparent", color: !clientForm.assignedTo ? "#16a34a" : "#334155", fontWeight: !clientForm.assignedTo ? 600 : 500, borderRadius: "6px", transition: "all 0.2s" }}
                            onMouseEnter={e => { if (clientForm.assignedTo) e.currentTarget.style.background = "#f1f5f9" }}
                            onMouseLeave={e => { if (clientForm.assignedTo) e.currentTarget.style.background = "transparent" }}
                          >
                            All Dealers
                          </div>
                          {employees
                            .filter(emp =>
                              ((emp.department && emp.department.toLowerCase().includes('sales')) ||
                                (emp.role && emp.role.name && emp.role.name.toLowerCase().includes('sales'))) &&
                              (`${emp.firstName} ${emp.lastName}`.toLowerCase().includes(dealerSearchTerm.toLowerCase()))
                            )
                            .map((emp) => (
                              <div
                                key={emp._id}
                                onClick={() => { updateFormField(setClientForm, setClientFormErrors, "assignedTo", emp._id); setShowDealerDropdown(false); setDealerSearchTerm(""); }}
                                style={{ padding: "8px 12px", fontSize: "12px", cursor: "pointer", background: clientForm.assignedTo === emp._id ? "#dcfce7" : "transparent", color: clientForm.assignedTo === emp._id ? "#16a34a" : "#334155", fontWeight: clientForm.assignedTo === emp._id ? 600 : 500, borderRadius: "6px", transition: "all 0.2s" }}
                                onMouseEnter={e => { if (clientForm.assignedTo !== emp._id) e.currentTarget.style.background = "#f1f5f9" }}
                                onMouseLeave={e => { if (clientForm.assignedTo !== emp._id) e.currentTarget.style.background = "transparent" }}
                              >
                                {emp.firstName} {emp.lastName}
                              </div>
                            ))}
                        </div>
                      </div>
                    )}
                  </div>
                </label>

                <label style={{ gridColumn: "1 / -1", display: "flex", flexDirection: "column", gap: "6px", fontSize: "12px", fontWeight: 700, color: "#334155" }}>
                  Requirement / Notes
                  <textarea
                    rows={5}
                    value={clientForm.requirement}
                    onChange={(event) => updateFormField(setClientForm, setClientFormErrors, "requirement", event.target.value)}
                    placeholder="Describe the client requirement"
                    aria-invalid={Boolean(clientFormErrors.requirement)}
                    style={{
                      borderRadius: "12px",
                      border: clientFormErrors.requirement ? "1px solid #ef4444" : "1px solid #e2e8f0",
                      padding: "12px 14px",
                      fontSize: "13px",
                      color: "#0f172a",
                      resize: "vertical",
                      fontFamily: "'Inter', 'Poppins', 'Segoe UI', sans-serif",
                    }}
                  />
                  {clientFormErrors.requirement && (
                    <span style={{ fontSize: "11px", color: "#dc2626", fontWeight: 600 }}>
                      {clientFormErrors.requirement}
                    </span>
                  )}
                </label>
              </div>

              <div style={{ padding: "16px 22px", borderTop: "1px solid #f1f5f9", display: "flex", justifyContent: "flex-end", gap: "10px" }}>
                <button
                  type="button"
                  onClick={closeAddClientModal}
                  className="clients-cancel-btn"
                  style={{
                    padding: "9px 14px",
                    borderRadius: "10px",
                    border: "1px solid #e2e8f0",
                    background: "#fff",
                    color: "#475569",
                    fontSize: "12px",
                    fontWeight: 700,
                    cursor: "pointer",
                  }}
                >
                  <span style={{ position: "relative", zIndex: 1 }}>Cancel</span>
                </button>
                <button
                  type="submit"
                  disabled={savingClient}
                  style={{
                    padding: "9px 14px",
                    borderRadius: "10px",
                    border: "none",
                    background: "linear-gradient(135deg, #056098 0%, #56a07a 100%)",
                    color: "#fff",
                    fontSize: "13px",
                    fontWeight: 600,
                    cursor: savingClient ? "not-allowed" : "pointer",
                    opacity: savingClient ? 0.6 : 1,
                  }}
                >
                  {savingClient ? "Saving..." : "Add"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── View Client Modal (read-only) ── */}
      {showViewModal && viewingClient && (
        <div onClick={() => { setShowViewModal(false); setViewingClient(null); }}
          style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1300, padding: '20px' }}>
          <div onClick={e => e.stopPropagation()}
            style={{ width: '100%', maxWidth: '520px', background: '#fff', borderRadius: '18px', border: '1px solid #e2e8f0', boxShadow: '0 24px 48px rgba(15,23,42,0.18)', overflow: 'hidden' }}>
            {/* Header */}
            <div style={{ padding: '18px 22px', borderBottom: '1px solid #f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <div style={{ width: '40px', height: '40px', borderRadius: '12px', background: 'linear-gradient(135deg, #056098 0%, #56a07a 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '16px', fontWeight: 800, color: '#fff', flexShrink: 0 }}>
                  {(viewingClient.name || viewingClient.company || '?')[0].toUpperCase()}
                </div>
                <div>
                  <div style={{ fontSize: '16px', fontWeight: 800, color: '#0f172a' }}>{viewingClient.name || '—'}</div>
                  <div style={{ fontSize: '11px', color: '#94a3b8', marginTop: '2px' }}>{viewingClient.company || ''}</div>
                </div>
              </div>
              <button onClick={() => { setShowViewModal(false); setViewingClient(null); }}
                style={{ background: 'transparent', border: 'none', fontSize: '22px', color: '#94a3b8', cursor: 'pointer', lineHeight: 1 }}>×</button>
            </div>
            {/* Body */}
            <div style={{ padding: '18px 22px', display: 'flex', flexDirection: 'column', gap: '0' }}>
              <p style={{ fontSize: '9px', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.8px', margin: '0 0 10px 0', paddingBottom: '6px', borderBottom: '1px solid #e2e8f0' }}>
                Client Details
              </p>
              {[
                { label: 'Name', value: viewingClient.name },
                { label: 'Company', value: viewingClient.company },
                { label: 'Phone', value: viewingClient.phone },
                { label: 'Email', value: viewingClient.email },
                { label: 'Requirement', value: viewingClient.requirement },
                { label: 'Source', value: viewingClient.source },
                { label: 'Type', value: viewingClient.recordType === 'meta' ? 'Meta Lead' : viewingClient.recordType === 'crm' ? 'CRM Lead' : 'Manual' },
                { label: 'Dealer', value: viewingClient.assignedTo },
                { label: 'Converted On', value: formatDate(viewingClient.convertedOn) },
              ].map(({ label, value }) => (
                <div key={label} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', padding: '7px 0', borderBottom: '1px solid #f8fafc' }}>
                  <span style={{ fontSize: '11px', color: '#94a3b8', fontWeight: 500, minWidth: '110px' }}>{label}</span>
                  <span style={{ fontSize: '11px', color: value && value !== '-' && value !== '—' ? '#1e293b' : '#94a3b8', fontWeight: 500, textAlign: 'right', maxWidth: '300px', wordBreak: 'break-word' }}>
                    {value && value !== '-' && value !== '—' ? value : '—'}
                  </span>
                </div>
              ))}
            </div>
            {/* Footer */}
            <div style={{ padding: '14px 22px', borderTop: '1px solid #f1f5f9', display: 'flex', justifyContent: 'flex-end' }}>
              <button onClick={() => { setShowViewModal(false); setViewingClient(null); }}
                style={{ padding: '9px 20px', borderRadius: '10px', border: '1px solid #e2e8f0', background: '#fff', color: '#475569', fontSize: '12px', fontWeight: 700, cursor: 'pointer', fontFamily: "'Inter','Poppins','Segoe UI',sans-serif" }}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {showEditModal && editingClient && (
        <div
          onClick={closeEditClientModal}
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(15, 23, 42, 0.45)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "20px",
            zIndex: 1000,
          }}
        >
          <div
            onClick={(event) => event.stopPropagation()}
            style={{
              width: "100%",
              maxWidth: "680px",
              background: "#fff",
              borderRadius: "18px",
              border: "1px solid #e2e8f0",
              boxShadow: "0 20px 40px rgba(15,23,42,0.18)",
              overflow: "hidden",
            }}
          >
            <div style={{ padding: "18px 22px", borderBottom: "1px solid #f1f5f9", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div>
                <div style={{ fontSize: "18px", fontWeight: 800, color: "#0f172a" }}>Edit Client</div>
                <div style={{ fontSize: "12px", color: "#94a3b8", marginTop: "4px" }}>
                  {editingClient.recordType === "meta"
                    ? "Meta client core fields are read-only here. You can update notes."
                    : "Update client details and keep it in the converted list."}
                </div>
              </div>
              <button
                onClick={closeEditClientModal}
                style={{ background: "transparent", border: "none", fontSize: "24px", color: "#94a3b8", cursor: "pointer", lineHeight: 1 }}
              >
                ×
              </button>
            </div>

            <form onSubmit={handleEditClient}>
              <div style={{ padding: "24px 28px", display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: "18px" }}>
                {[
                  { label: "Client Name", key: "name", required: true, placeholder: "Enter client name", type: "text" },
                  { label: "Company", key: "company", required: false, placeholder: "Enter company name", type: "text" },
                  { label: "Phone", key: "phone", required: false, placeholder: "Enter phone number", type: "tel" },
                  { label: "Email", key: "email", required: false, placeholder: "Enter email address", type: "email" },
                  { label: "Source", key: "source", required: false, placeholder: "Manual", type: "text" },
                ].map((field) => {
                  const isReadOnly = editingClient.recordType === "meta";
                  return (
                    <label key={field.key} style={{ display: "flex", flexDirection: "column", gap: "6px", fontSize: "12px", fontWeight: 700, color: "#334155" }}>
                      <span>{field.label} {field.required && <span style={{ color: "#ef4444" }}>*</span>}</span>
                      <input
                        type={field.type}
                        required={field.required}
                        value={editForm[field.key]}
                        readOnly={isReadOnly}
                        onChange={(event) => updateFormField(setEditForm, setEditFormErrors, field.key, event.target.value)}
                        placeholder={field.placeholder}
                        aria-invalid={Boolean(editFormErrors[field.key])}
                        style={{
                          height: "42px",
                          borderRadius: "10px",
                          border: editFormErrors[field.key] ? "1px solid #ef4444" : "1px solid #e2e8f0",
                          padding: "0 14px",
                          fontSize: "13px",
                          color: isReadOnly ? "#64748b" : "#0f172a",
                          background: isReadOnly ? "#f8fafc" : "#fff",
                          fontFamily: "'Inter', 'Poppins', 'Segoe UI', sans-serif",
                        }}
                      />
                      {editFormErrors[field.key] && (
                        <span style={{ fontSize: "11px", color: "#dc2626", fontWeight: 600 }}>
                          {editFormErrors[field.key]}
                        </span>
                      )}
                    </label>
                  );
                })}

                <label style={{ display: "flex", flexDirection: "column", gap: "6px", fontSize: "12px", fontWeight: 700, color: "#334155" }}>
                  Dealer
                  <div style={{ position: "relative" }}>
                    <div
                      onClick={() => setShowEditDealerDropdown(!showEditDealerDropdown)}
                      style={{
                        height: "42px",
                        borderRadius: "10px",
                        border: editFormErrors.assignedTo ? "1px solid #ef4444" : "1px solid #e2e8f0",
                        padding: "0 14px",
                        fontSize: "13px",
                        color: "#0f172a",
                        background: "#fff",
                        fontFamily: "'Inter', 'Poppins', 'Segoe UI', sans-serif",
                        display: "flex",
                        alignItems: "center",
                        cursor: "pointer",
                        justifyContent: "space-between"
                      }}
                    >
                      <span>
                        {editForm.assignedTo
                          ? (() => {
                            const emp = employees.find((e) => e._id === editForm.assignedTo);
                            return emp ? `${emp.firstName} ${emp.lastName}` : "Select Dealer";
                          })()
                          : "Select Dealer"}
                      </span>
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9"></polyline></svg>
                    </div>

                    {showEditDealerDropdown && (
                      <div style={{ position: "absolute", top: "100%", left: 0, width: "100%", background: "#fff", zIndex: 100, border: "1px solid #e2e8f0", borderRadius: "10px", marginTop: "4px", boxShadow: "0 10px 15px -3px rgba(0, 0, 0, 0.1)", padding: "6px" }}>
                        <div style={{ marginBottom: "6px" }}>
                          <input
                            type="text"
                            placeholder="Search dealers..."
                            value={dealerSearchTerm}
                            onChange={e => setDealerSearchTerm(e.target.value)}
                            onClick={e => e.stopPropagation()}
                            style={{ width: "100%", border: "none", background: "#f8fafc", padding: "8px 12px", borderRadius: "8px", fontSize: "12px", outline: "none", fontFamily: "'Inter', 'Poppins', 'Segoe UI', sans-serif" }}
                          />
                        </div>
                        <div style={{ maxHeight: "160px", overflowY: "auto", display: "flex", flexDirection: "column", gap: "2px" }}>
                          <div
                            onClick={() => { updateFormField(setEditForm, setEditFormErrors, "assignedTo", ""); setShowEditDealerDropdown(false); setDealerSearchTerm(""); }}
                            style={{ padding: "8px 12px", fontSize: "12px", cursor: "pointer", background: !editForm.assignedTo ? "#dcfce7" : "transparent", color: !editForm.assignedTo ? "#16a34a" : "#334155", fontWeight: !editForm.assignedTo ? 600 : 500, borderRadius: "6px", transition: "all 0.2s" }}
                            onMouseEnter={e => { if (editForm.assignedTo) e.currentTarget.style.background = "#f1f5f9" }}
                            onMouseLeave={e => { if (editForm.assignedTo) e.currentTarget.style.background = "transparent" }}
                          >
                            All Dealers
                          </div>
                          {employees
                            .filter(emp =>
                              ((emp.department && emp.department.toLowerCase().includes('sales')) ||
                                (emp.role && emp.role.name && emp.role.name.toLowerCase().includes('sales'))) &&
                              (`${emp.firstName} ${emp.lastName}`.toLowerCase().includes(dealerSearchTerm.toLowerCase()))
                            )
                            .map((emp) => (
                              <div
                                key={emp._id}
                                onClick={() => { updateFormField(setEditForm, setEditFormErrors, "assignedTo", emp._id); setShowEditDealerDropdown(false); setDealerSearchTerm(""); }}
                                style={{ padding: "8px 12px", fontSize: "12px", cursor: "pointer", background: editForm.assignedTo === emp._id ? "#dcfce7" : "transparent", color: editForm.assignedTo === emp._id ? "#16a34a" : "#334155", fontWeight: editForm.assignedTo === emp._id ? 600 : 500, borderRadius: "6px", transition: "all 0.2s" }}
                                onMouseEnter={e => { if (editForm.assignedTo !== emp._id) e.currentTarget.style.background = "#f1f5f9" }}
                                onMouseLeave={e => { if (editForm.assignedTo !== emp._id) e.currentTarget.style.background = "transparent" }}
                              >
                                {emp.firstName} {emp.lastName}
                              </div>
                            ))}
                        </div>
                      </div>
                    )}
                  </div>
                </label>

                <label style={{ gridColumn: "1 / -1", display: "flex", flexDirection: "column", gap: "6px", fontSize: "12px", fontWeight: 700, color: "#334155" }}>
                  Requirement / Notes
                  <textarea
                    rows={5}
                    value={editForm.requirement}
                    onChange={(event) => updateFormField(setEditForm, setEditFormErrors, "requirement", event.target.value)}
                    placeholder="Describe the client requirement"
                    aria-invalid={Boolean(editFormErrors.requirement)}
                    style={{
                      borderRadius: "12px",
                      border: editFormErrors.requirement ? "1px solid #ef4444" : "1px solid #e2e8f0",
                      padding: "12px 14px",
                      fontSize: "13px",
                      color: "#0f172a",
                      resize: "vertical",
                      fontFamily: "'Inter', 'Poppins', 'Segoe UI', sans-serif",
                    }}
                  />
                  {editFormErrors.requirement && (
                    <span style={{ fontSize: "11px", color: "#dc2626", fontWeight: 600 }}>
                      {editFormErrors.requirement}
                    </span>
                  )}
                </label>
              </div>

              <div style={{ padding: "16px 22px", borderTop: "1px solid #f1f5f9", display: "flex", justifyContent: "flex-end", gap: "10px" }}>
                <button
                  type="button"
                  onClick={closeEditClientModal}
                  className="clients-cancel-btn"
                  style={{
                    padding: "9px 14px",
                    borderRadius: "10px",
                    border: "1px solid #e2e8f0",
                    background: "#fff",
                    color: "#475569",
                    fontSize: "12px",
                    fontWeight: 700,
                    cursor: "pointer",
                  }}
                >
                  <span style={{ position: "relative", zIndex: 1 }}>Cancel</span>
                </button>
                <button
                  type="submit"
                  disabled={savingEditClient}
                  style={{
                    padding: "9px 14px",
                    borderRadius: "10px",
                    border: "none",
                    background: "linear-gradient(135deg, #056098 0%, #56a07a 100%)",
                    color: "#fff",
                    fontSize: "12px",
                    fontWeight: 700,
                    cursor: savingEditClient ? "not-allowed" : "pointer",
                    opacity: savingEditClient ? 0.6 : 1,
                  }}
                >
                  {savingEditClient ? "Saving..." : "Save Changes"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Projects Modal */}
      {selectedClientForProjects && (() => {
        const clientProjs = projects.filter(
          (p) =>
            (p.clientName && selectedClientForProjects.name && p.clientName.toLowerCase() === selectedClientForProjects.name.toLowerCase()) ||
            (p.clientEmail && selectedClientForProjects.email && p.clientEmail.toLowerCase() === selectedClientForProjects.email.toLowerCase()) ||
            (p.clientPhone && selectedClientForProjects.phone && p.clientPhone === selectedClientForProjects.phone)
        );

        return (
          <div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: "rgba(15, 23, 42, 0.4)",
              backdropFilter: "blur(4px)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 1300,
              padding: "20px",
              fontFamily: "'Inter', 'Poppins', 'Segoe UI', sans-serif"
            }}
            onClick={() => setSelectedClientForProjects(null)}
          >
            <div
              style={{
                background: "#fff",
                borderRadius: "16px",
                width: "100%",
                maxWidth: "800px",
                maxHeight: "85vh",
                minHeight: "300px",
                display: "flex",
                flexDirection: "column",
                boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1)",
                overflow: "hidden"
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <div
                style={{
                  padding: "20px 24px",
                  borderBottom: "1px solid #f1f5f9",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center"
                }}
              >
                <div>
                  <h2 style={{ margin: 0, fontSize: "18px", fontWeight: 700, color: "#0f172a" }}>
                    Projects for {selectedClientForProjects.name}
                  </h2>
                  <p style={{ margin: "4px 0 0", fontSize: "13px", color: "#64748b" }}>
                    {selectedClientForProjects.company}
                  </p>
                </div>
                <button
                  onClick={() => setSelectedClientForProjects(null)}
                  style={{
                    border: "none",
                    background: "#f1f5f9",
                    borderRadius: "50%",
                    width: "32px",
                    height: "32px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    cursor: "pointer",
                    color: "#64748b",
                    transition: "background 0.2s, color 0.2s"
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = "#e2e8f0"; e.currentTarget.style.color = "#0f172a"; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = "#f1f5f9"; e.currentTarget.style.color = "#64748b"; }}
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="18" y1="6" x2="6" y2="18"></line>
                    <line x1="6" y1="6" x2="18" y2="18"></line>
                  </svg>
                </button>
              </div>

              <div style={{ padding: "12px", overflowY: "auto", flex: 1, background: "#ffffff" }}>
                {clientProjs.length === 0 ? (
                  <div style={{ textAlign: "center", padding: "20px" }}>
                    <div style={{ fontSize: "12px", fontWeight: 500, color: "#111827", marginBottom: "2px" }}>No projects found</div>
                    <div style={{ fontSize: "11px", color: "#6b7280" }}>This client doesn't have any active or past projects yet.</div>
                  </div>
                ) : (
                  <div style={{ background: "#ffffff", borderRadius: "4px", border: "1px solid #e2e8f0", overflow: "hidden" }}>
                    <table style={{ width: "100%", borderCollapse: "collapse", textAlign: "left" }}>
                      <thead>
                        <tr>
                          <th style={{ padding: "6px 10px", color: "#475569", fontSize: "10px", fontWeight: 600, borderBottom: "1px solid #e2e8f0", width: "30px" }}>#</th>
                          <th style={{ padding: "6px 10px", color: "#475569", fontSize: "10px", fontWeight: 600, borderBottom: "1px solid #e2e8f0" }}>Project ID</th>
                          <th style={{ padding: "6px 10px", color: "#475569", fontSize: "10px", fontWeight: 600, borderBottom: "1px solid #e2e8f0" }}>Project Name</th>
                          <th style={{ padding: "6px 10px", color: "#475569", fontSize: "10px", fontWeight: 600, borderBottom: "1px solid #e2e8f0" }}>Project Value</th>
                          <th style={{ padding: "6px 10px", color: "#475569", fontSize: "10px", fontWeight: 600, borderBottom: "1px solid #e2e8f0" }}>Deadline</th>
                          <th style={{ padding: "6px 10px", color: "#475569", fontSize: "10px", fontWeight: 600, borderBottom: "1px solid #e2e8f0" }}>Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {clientProjs.map((proj, index) => (
                          <tr key={proj._id}
                            onClick={() => setSelectedProjectForAssignment(proj)}
                            style={{ borderBottom: index === clientProjs.length - 1 ? 'none' : '1px solid #f1f5f9', cursor: 'pointer', transition: 'background-color 0.15s ease' }}
                            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = "#f8fafc"}
                            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = "transparent"}
                          >
                            <td style={{ padding: "6px 10px", verticalAlign: "middle", width: "30px" }}>
                              <div style={{ fontSize: "11px", fontWeight: 600, color: "#94a3b8" }}>{String(index + 1).padStart(2, '0')}</div>
                            </td>
                            <td style={{ padding: "6px 10px", verticalAlign: "middle" }}>
                              <div style={{ fontSize: "11px", color: "#64748b", fontWeight: 500 }}>{proj.projectId || '-'}</div>
                            </td>
                            <td style={{ padding: "6px 10px", verticalAlign: "middle" }}>
                              <div style={{ fontSize: "11px", fontWeight: 500, color: "#1e293b" }}>{proj.name}</div>
                            </td>
                            <td style={{ padding: "6px 10px", verticalAlign: "middle" }}>
                              <div style={{ fontSize: "11px", fontWeight: 500, color: "#1e293b" }}>
                                {proj.projectValue ? new Intl.NumberFormat('en-IN', { style: 'currency', currency: proj.currency || 'INR', maximumFractionDigits: 0 }).format(proj.projectValue) : '-'}
                              </div>
                            </td>
                            <td style={{ padding: "6px 10px", verticalAlign: "middle" }}>
                              <div style={{ fontSize: "11px", fontWeight: 500, color: "#1e293b" }}>
                                {proj.deadline ? new Date(proj.deadline).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : 'N/A'}
                              </div>
                            </td>
                            <td style={{ padding: "6px 10px", verticalAlign: "middle" }}>
                              {(() => {
                                const st = (proj.status || '').toLowerCase();
                                let colors = { bg: '#f3f4f6', border: '#e5e7eb', text: '#4b5563' }; // default grey

                                if (st === 'completed') colors = { bg: '#dcfce7', border: '#bbf7d0', text: '#16a34a' }; // green
                                else if (st === 'cancelled') colors = { bg: '#fee2e2', border: '#fecaca', text: '#dc2626' }; // red
                                else if (st === 'on-hold' || st === 'on hold') colors = { bg: '#fef9c3', border: '#fef08a', text: '#ca8a04' }; // yellow
                                else if (st === 'progress' || st === 'active') colors = { bg: '#dbeafe', border: '#bfdbfe', text: '#2563eb' }; // blue

                                return (
                                  <div style={{
                                    display: "inline-flex", alignItems: "center", gap: "4px",
                                    background: colors.bg, padding: "2px 6px", borderRadius: "4px", border: `1px solid ${colors.border}`
                                  }}>
                                    <div style={{ width: "4px", height: "4px", borderRadius: "50%", background: colors.text }} />
                                    <span style={{ fontSize: "9px", color: colors.text, fontWeight: 600, letterSpacing: "0.2px", textTransform: "uppercase" }}>
                                      {st === 'active' ? 'PROGRESS' : (proj.status || 'PROGRESS')}
                                    </span>
                                  </div>
                                );
                              })()}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          </div>
        );
      })()}
      {/* Assignment Details Modal */}
      {selectedProjectForAssignment && (() => {
        // Find assigned employees full objects based on assignedEmployees array
        const assignedEmployeesInfo = selectedProjectForAssignment.assignedEmployees
          ? employees.filter(emp => selectedProjectForAssignment.assignedEmployees.some(
            assigned => (typeof assigned === 'object' && assigned._id ? assigned._id : assigned) === emp._id
          ))
          : [];

        return (
          <div
            style={{
              position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
              background: "rgba(15, 23, 42, 0.5)", backdropFilter: "blur(4px)",
              display: "flex", alignItems: "center", justifyContent: "center",
              zIndex: 1400, padding: "20px", fontFamily: "'Inter', 'Poppins', 'Segoe UI', sans-serif"
            }}
            onClick={() => setSelectedProjectForAssignment(null)}
          >
            <div
              style={{
                background: "#fff", borderRadius: "8px", width: "100%", maxWidth: "540px", maxHeight: "85vh",
                display: "flex", flexDirection: "column", boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1)",
                overflow: "hidden"
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <div style={{ padding: "16px", display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                <div>
                  <h2 style={{ margin: 0, fontSize: "16px", fontWeight: 700, color: "#111827", letterSpacing: "-0.5px" }}>Working Details</h2>
                  <p style={{ margin: "2px 0 0", fontSize: "12px", color: "#6b7280" }}>{selectedProjectForAssignment.name}</p>
                </div>
                <button
                  onClick={() => setSelectedProjectForAssignment(null)}
                  style={{
                    border: "none", background: "#f3f4f6", borderRadius: "50%", width: "28px", height: "28px",
                    display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", color: "#6b7280",
                    transition: "background 0.2s, color 0.2s"
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = "#e5e7eb"; e.currentTarget.style.color = "#111827"; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = "#f3f4f6"; e.currentTarget.style.color = "#6b7280"; }}
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="18" y1="6" x2="6" y2="18"></line>
                    <line x1="6" y1="6" x2="18" y2="18"></line>
                  </svg>
                </button>
              </div>

              <div style={{ padding: "0 16px 16px", overflowY: "auto", flex: 1, background: "#ffffff" }}>
                <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
                  <div style={{ background: "#ffffff", borderRadius: "6px", padding: "12px 16px", border: "1px solid #e5e7eb" }}>
                    <div style={{ fontSize: "11px", color: "#6b7280", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: "12px" }}>Assigned Employees</div>
                    {assignedEmployeesInfo.length > 0 ? (
                      <div style={{ display: "flex", flexDirection: "column" }}>
                        {assignedEmployeesInfo.map((emp, index) => {
                          const assignmentDate = selectedProjectForAssignment.employeeAssignmentDates?.[emp._id];
                          return (
                            <div key={emp._id} style={{ display: "flex", alignItems: "center", gap: "12px", padding: "8px 0", borderBottom: index === assignedEmployeesInfo.length - 1 ? 'none' : '1px solid #f3f4f6' }}>
                              <div style={{ width: "32px", height: "32px", borderRadius: "50%", background: "#e2e8f0", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "12px", fontWeight: 600, color: "#475569" }}>
                                {emp.firstName?.charAt(0)}{emp.lastName?.charAt(0)}
                              </div>
                              <div>
                                <div style={{ fontSize: "13px", fontWeight: 600, color: "#111827" }}>{emp.firstName} {emp.lastName}</div>
                                <div style={{ fontSize: "11px", color: "#6b7280" }}>{emp.designation || emp.role?.name || 'Employee'}</div>
                              </div>
                              <div style={{ marginLeft: "auto", textAlign: "right" }}>
                                <div style={{ fontSize: "9px", color: "#9ca3af", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: "2px" }}>Assigned On</div>
                                <div style={{ fontSize: "11px", color: "#4b5563", fontWeight: 500 }}>
                                  {assignmentDate ? new Date(assignmentDate).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : 'N/A'}
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <div style={{ fontSize: "12px", color: "#6b7280", fontStyle: "italic" }}>No employees assigned yet.</div>
                    )}
                  </div>

                  <div
                    onClick={() => setShowRequirementModal(true)}
                    style={{
                      background: "#ffffff", borderRadius: "6px", padding: "12px 16px", border: "1px solid #e5e7eb",
                      cursor: "pointer", transition: "all 0.2s"
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.borderColor = "#cbd5e1"; e.currentTarget.style.boxShadow = "0 2px 4px rgba(0,0,0,0.02)"; }}
                    onMouseLeave={(e) => { e.currentTarget.style.borderColor = "#e5e7eb"; e.currentTarget.style.boxShadow = "none"; }}
                  >
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "8px" }}>
                      <div style={{ fontSize: "11px", color: "#6b7280", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em" }}>Project Requirement / Notes</div>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M15 3h6v6M9 21H3v-6M21 3l-7 7M3 21l7-7" />
                      </svg>
                    </div>
                    <div style={{ fontSize: "13px", color: "#374151", lineHeight: 1.5, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                      {selectedProjectForAssignment.description || selectedProjectForAssignment.requirement || "No notes available."}
                    </div>
                  </div>

                  <div style={{ background: "#ffffff", borderRadius: "6px", padding: "12px 16px", border: "1px solid #e5e7eb", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <div>
                      <div style={{ fontSize: "11px", color: "#6b7280", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: "4px" }}>Start Date</div>
                      <div style={{ fontSize: "13px", color: "#111827", fontWeight: 500 }}>{selectedProjectForAssignment.startDate ? new Date(selectedProjectForAssignment.startDate).toLocaleDateString('en-IN') : 'N/A'}</div>
                    </div>

                    <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", padding: "0 30px" }}>
                      <style>
                        {`
                          @keyframes pulseRight {
                            0% { transform: translateX(-4px); opacity: 0.5; }
                            50% { transform: translateX(4px); opacity: 1; }
                            100% { transform: translateX(-4px); opacity: 0.5; }
                          }
                        `}
                      </style>
                      <div style={{ flex: 1, height: "1px", background: "linear-gradient(90deg, transparent, #e2e8f0, transparent)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                        <svg
                          style={{ animation: "pulseRight 2s infinite ease-in-out", color: "#94a3b8", background: "#fff", padding: "0 8px" }}
                          width="30" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
                        >
                          <line x1="5" y1="12" x2="19" y2="12"></line>
                          <polyline points="12 5 19 12 12 19"></polyline>
                        </svg>
                      </div>
                    </div>

                    <div style={{ textAlign: "right" }}>
                      <div style={{ fontSize: "11px", color: "#6b7280", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: "4px" }}>Deadline</div>
                      <div style={{ fontSize: "13px", color: "#111827", fontWeight: 500 }}>{selectedProjectForAssignment.deadline ? new Date(selectedProjectForAssignment.deadline).toLocaleDateString('en-IN') : 'N/A'}</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );
      })()}
      {/* Requirement Full Text Modal */}
      {showRequirementModal && selectedProjectForAssignment && (
        <div
          style={{
            position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
            background: "rgba(15, 23, 42, 0.6)", backdropFilter: "blur(4px)",
            display: "flex", alignItems: "center", justifyContent: "center",
            zIndex: 1500, padding: "20px", fontFamily: "'Inter', 'Poppins', 'Segoe UI', sans-serif"
          }}
          onClick={() => setShowRequirementModal(false)}
        >
          <div
            style={{
              background: "#fff", borderRadius: "8px", width: "100%", maxWidth: "600px", maxHeight: "85vh",
              display: "flex", flexDirection: "column", boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
              overflow: "hidden"
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ padding: "16px 24px", borderBottom: "1px solid #f1f5f9", display: "flex", justifyContent: "space-between", alignItems: "center", background: "#f8fafc" }}>
              <h2 style={{ margin: 0, fontSize: "16px", fontWeight: 600, color: "#0f172a" }}>Project Requirement</h2>
              <button
                onClick={() => setShowRequirementModal(false)}
                style={{
                  border: "none", background: "transparent", width: "28px", height: "28px",
                  display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", color: "#64748b",
                  borderRadius: "4px"
                }}
                onMouseEnter={(e) => e.currentTarget.style.background = "#e2e8f0"}
                onMouseLeave={(e) => e.currentTarget.style.background = "transparent"}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
              </button>
            </div>
            <div style={{ padding: "24px", overflowY: "auto", fontSize: "14px", color: "#334155", lineHeight: 1.6, whiteSpace: "pre-wrap" }}>
              {selectedProjectForAssignment.description || selectedProjectForAssignment.requirement || "No notes available."}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Clients;
